import { formatMoney } from '../../utils/stringUtils';
import requestService from '../../services/request';
import {
  CONFIG,
  FEATURE_TOGGLES,
  FINANCIAL_POSITION,
  ROLES,
  UNIT,
  USER,
} from '../../utils/constants';

let financialPositionPromise = null;

export default {
  state: {
    selectedContract: undefined,
    error: false,
    loading: false,
    loadingPaid: false,
    loadingLate: false,
    loadingToExpire: false,
    loadingTotals: false,
    financialPositionData: {
      late: [],
      toExpire: [],
      paid: [],
      paidTotal: null,
      toExpireTotal: null,
      lateTotal: null,
      hasPayableLatePaymentSlips: false,
      baseDate: null,
    },
    financialSummaryItems: {
      lateTotal: null,
      paidTotal: null,
      toExpireTotal: null,
    },
    unitFinancialPositions: {
      // [contractId]: {
      //  "contractStatus": "Ativo",
      //  "contractType": "Avulso",
      //  "isMain": false,
      //   late: [],
      //   toExpire: [],
      //   paid: [],
      //   paidTotal: null,
      //   toExpireTotal: null,
      //   lateTotal: null,
      //   hasPayableLatePaymentSlips: false,
      //   baseDate: null,
      // },
    },
  },
  getters: {
    [FINANCIAL_POSITION.GET_UNIT_POSITIONS]: (state) =>
      state.unitFinancialPositions,
    [FINANCIAL_POSITION.GET_DATA]: (state) => state.financialPositionData,
    [FINANCIAL_POSITION.GET_SUMMARY]: (state) => state.financialSummaryItems,

    [FINANCIAL_POSITION.LOADING]: (state) => state.loading,
    [FINANCIAL_POSITION.LOADING_LATE]: (state) => state.loadingLate,
    [FINANCIAL_POSITION.LOADING_PAID]: (state) => state.loadingPaid,
    [FINANCIAL_POSITION.LOADING_TO_EXPIRE]: (state) => state.loadingToExpire,
    [FINANCIAL_POSITION.LOADING_TOTALS]: (state) => state.loadingTotals,
    [FINANCIAL_POSITION.ERROR]: (state) => state.error,
  },
  mutations: {
    SET_LOADING(state, isLoading) {
      // Você pode ter mutações específicas para cada loading, ou um geral
      state.loading = isLoading;
    },
    SET_ERROR(state, hasError) {
      state.error = hasError;
    },
    SET_LOADING_SUBSTATES(
      state,
      { loadingToExpire, loadingLate, loadingPaid, loadingTotals }
    ) {
      state.loadingToExpire = loadingToExpire;
      state.loadingLate = loadingLate;
      state.loadingPaid = loadingPaid;
      state.loadingTotals = loadingTotals;
    },
    [FINANCIAL_POSITION.SET_UNIT_POSITIONS]: (state, payload) =>
      (state.unitFinancialPositions = payload),
    [FINANCIAL_POSITION.SET_DATA]: (state, mainPositionData) => {
      state.financialPositionData = mainPositionData;

      state.financialSummaryItems = {
        lateTotal: formatMoney(mainPositionData.lateTotal),
        paidTotal: formatMoney(mainPositionData.paidTotal),
        toExpireTotal: formatMoney(mainPositionData.toExpireTotal),
      };
    },
  },
  actions: {
    [FINANCIAL_POSITION.FETCH_POSITION]: async (
      { state, getters, commit, dispatch },
      { contract, getSlipsUrl = true }
    ) => {
      if (getters[FEATURE_TOGGLES.UNIT_FINANCIAL_POSITION]) {
        return await dispatch(FINANCIAL_POSITION.FETCH_BY_UNIT, {
          contract,
          getSlipsUrl,
        });
      } else {
        return await dispatch(FINANCIAL_POSITION.FETCH_BY_CONTRACT, {
          contract,
          getSlipsUrl,
        });
      }
    },

    async [FINANCIAL_POSITION.FETCH_BY_UNIT](
      { state, getters, commit },
      { contract, getSlipsUrl }
    ) {
      if (getters[USER.GET_USER_ROLE] !== ROLES.CLIENT_ROLE) return;

      if (state.unitFinancialPositions[contract.id]) {
        commit(
          FINANCIAL_POSITION.SET_DATA,
          state.unitFinancialPositions[contract.id]
        );
        return state.unitFinancialPositions[contract.id];
      }

      if (financialPositionPromise) {
        return await financialPositionPromise;
      }

      financialPositionPromise = (async () => {
        try {
          commit('SET_LOADING', true);
          commit('SET_ERROR', false);

          const selectedUnit = getters[UNIT.GET_SELECTED];

          const params = {
            contractType: contract.type,
            productCode: selectedUnit.product.code,
            blockCode: selectedUnit.block.code,
            unitCode: selectedUnit.unit.code,
            getSlipsUrl: !!getSlipsUrl,
          };

          const unitPositions = await requestService(
            `${process.env.FINANCIAL_API_URL}/unit/${selectedUnit.unit.id}`,
            'GET',
            { params: { ...params }, localErrorHandling: true, retries: 1 }
          );

          const unitDict = unitPositions.reduce(
            (acc, { contractId, ...rest }) => {
              acc[contractId] = rest;
              return acc;
            },
            {}
          );

          commit(FINANCIAL_POSITION.SET_UNIT_POSITIONS, unitDict);
          commit(
            FINANCIAL_POSITION.SET_DATA,
            state.unitFinancialPositions[contract.id]
          );

          return state.financialPositionData;
        } catch (err) {
          commit('SET_ERROR', true);
          throw err;
        } finally {
          commit('SET_LOADING', false);
          financialPositionPromise = null;
        }
      })();

      return await financialPositionPromise;
    },

    async [FINANCIAL_POSITION.FETCH_BY_CONTRACT](
      { state, getters, commit },
      { contract, getSlipsUrl }
    ) {
      if (getters[USER.GET_USER_ROLE] !== ROLES.CLIENT_ROLE) return;

      if (contract.id === state.selectedContract && financialContractPromise)
        return await financialContractPromise;

      financialContractPromise = (async () => {
        try {
          commit('SET_LOADING', true);
          commit('SET_ERROR', false);
          commit('SET_SELECTED_CONTRACT', contract);
          commit('SET_LOADING_SUBSTATES', {
            loadingToExpire: true,
            loadingLate: true,
            loadingPaid: true,
            loadingTotals: true,
          });

          let contractPosition = {
            late: [],
            toExpire: [],
            paid: [],
            paidTotal: null,
            toExpireTotal: null,
            lateTotal: null,
            hasPayableLatePaymentSlips: false,
            baseDate: null,
          };

          const selectedUnit = getters[UNIT.GET_SELECTED];

          const params = {
            contractId: contract.id,
            contractType: contract.type,
            productCode: selectedUnit.product.code,
            blockCode: selectedUnit.block.code,
            unitCode: selectedUnit.unit.code,
            getSlipsUrl: !!getSlipsUrl,
          };

          const promises = ['toExpire', 'late', 'paid'].map((type) =>
            requestService(process.env.FINANCIAL_API_URL, 'GET', {
              params: { ...params, types: [type] },
              localErrorHandling: true,
              retries: 0,
            }).then((res) => {
              // Verifica se a resposta ainda é do contrato selecionado
              if (res.contractId === state.selectedContract.id.toString()) {
                contractPosition = { ...contractPosition, ...res };
                switch (type) {
                  case 'toExpire':
                    commit('SET_LOADING_SUBSTATES', {
                      ...state,
                      loadingToExpire: false,
                    });
                    break;
                  case 'late':
                    commit('SET_LOADING_SUBSTATES', {
                      ...state,
                      loadingLate: false,
                    });
                    break;
                  case 'paid':
                    commit('SET_LOADING_SUBSTATES', {
                      ...state,
                      loadingPaid: false,
                    });
                    break;
                }
                return res;
              }
            })
          );

          await Promise.all(promises);

          commit(FINANCIAL_POSITION.SET_DATA, contractPosition);

          return contractPosition;
        } catch (err) {
          commit('SET_ERROR', true);
          throw err;
        } finally {
          commit('SET_LOADING_SUBSTATES', {
            loadingToExpire: false,
            loadingLate: false,
            loadingPaid: false,
            loadingTotals: false,
          });
          commit('SET_LOADING', false);
          financialContractPromise = null;
        }
      })();

      return await financialContractPromise;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/financial.js